/**
 * What is actually being flown — evidence, not the schedule.
 *
 * The desk may not decide a ticket flew because it was next on the board.
 * Money (crew hotel, parking, charter revenue) and "completed" need layered
 * grounding, and they fail closed when the layers disagree:
 *
 *   1. Aircraft position (SimConnect): where the tail is sitting, or, when
 *      airborne, whether the fix is on the ticket's origin→dest corridor.
 *   2. Filed plan (SimBrief OFP on the ticket): origin/dest must be the pair
 *      being flown.
 *   3. SayIntentions session when the sim has one — corroboration only, never
 *      the sole source.
 *
 * Schedule (next STD, Inbox, "looks like this route") is not evidence.
 * Unknown is not the same as agreed: lifecycle may still move on unknown, but
 * ledger money may not.
 */
import { type TripAssignment } from "./assignment";
import { resolveCoords } from "./airports";
import { icaoKey } from "./curfew";
import { isNarniaGroundSpawn, resolveCurrentFlight, type FlightBindingAuthority } from "./flightBinding";
import { displayFlightNumber, FIRST_FLIGHT_SEQ, parseFlightNumber } from "./flightNumber";
import { haversineNm } from "./geo";
import {
  ticketLifecycle,
  type PickCurrentTicketInput,
} from "./opsBoard";
import { nearestDestination, type FusedPosition } from "./position";
import { sampleIsAirborne, type SimPhaseSample } from "./simDraft";
import type { SiFlightSession } from "./sayintentions";
import type {
  AircraftSpec,
  FlightLogEntry,
  Mission,
  MissionOfpAttachment,
  SceneryAirport,
} from "./types";

/** Sitting this close to a field counts as parked at it (same as the parked latch). */
export const FIELD_PRESENCE_NM = 8;
/**
 * A recorded fix speaks for "where the aircraft is now" this long. Older than
 * that it still anchors an overnight (Outcome E), but it no longer contradicts
 * a desk that is flying without SimConnect.
 */
export const PRESENCE_TRUST_MS = 12 * 3600_000;
/** Airborne fix is "on this route" inside origin→dest × factor + slack. */
export const OD_CORRIDOR_FACTOR = 1.6;
export const OD_CORRIDOR_SLACK_NM = 75;
/** Short hops: never call a fix off-route inside this bubble. */
export const OD_CORRIDOR_MIN_NM = 120;

export type MatchState = "match" | "mismatch" | "unknown";
export type GroundingVerdict = "grounded" | "unverified" | "mismatch";
export type GroundingSource = "simconnect" | "flight_plan" | "sayintentions" | "flight_log";

export type PresenceSource = "simconnect" | "sayintentions" | "vatsim" | "last_known" | "tail_record";

export type SimPresence = {
  /** Field the tail is sitting at right now (or last parked at). */
  atIcao: string | null;
  /** Nearest field to the fix, parked or not. */
  nearestIcao: string | null;
  distanceNm: number | null;
  airborne: boolean;
  /** Any live-or-remembered sim evidence at all (includes the tail record). */
  known: boolean;
  /** An actual position fix existed — not just the tail's remembered park. */
  fromSim: boolean;
  /** Fresh sim sample, good for "where the aircraft is right now". */
  live: boolean;
  /** Recent enough to contradict the desk. */
  trusted: boolean;
  ageMs: number | null;
  source: PresenceSource | null;
  atUtc: string | null;
  /** This sim session has been airborne at some point. */
  sawAirborne: boolean;
};

export type TicketGrounding = {
  missionId: string;
  flightNumber: string;
  originIcao: string;
  destIcao: string;
  verdict: GroundingVerdict;
  /** Sim says the tail is sitting at the ticket origin. */
  atOrigin: MatchState;
  /** Sim says the tail is sitting at the ticket dest. */
  atDest: MatchState;
  /** Airborne fix sits on the ticket's origin→dest corridor. */
  route: MatchState;
  /** Filed OFP origin/dest vs the ticket. */
  plan: MatchState;
  /** SayIntentions session origin/dest (or callsign) vs the ticket. */
  si: MatchState;
  /** This trip has its own recorded departure / arrival in the flight log. */
  log: MatchState;
  /**
   * This ticket already has a landing on the record (log arrival or a sim-driven
   * Arrived). The tail is free to move on afterwards without un-flying it.
   */
  arrivalRecorded: boolean;
  presence: SimPresence;
  sources: GroundingSource[];
  /** Hard disagreements — these fail closed. */
  conflicts: string[];
  status: string;
};

export type GroundTicketInput = {
  mission: Mission;
  /** Live / last-known fused fix for this desk's sim. */
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  simHistory?: ReadonlyArray<SimPhaseSample>;
  /** Tail record — `lastAirportIcao` is the remembered park after a SimConnect drop. */
  aircraft?: Pick<AircraftSpec, "id" | "registration" | "lastAirportIcao"> | null;
  fleet?: ReadonlyArray<AircraftSpec>;
  logs?: ReadonlyArray<FlightLogEntry>;
  /** Live SayIntentions session, when the sim has one. */
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null;
  /** Filed plan. Defaults to the ticket's own OFP attachment. */
  ofp?: MissionOfpAttachment | null;
  /** Whole board — used for CEL identity hygiene. */
  missions?: ReadonlyArray<Mission>;
  now?: Date;
};

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

function asMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function sortedHistory(history: ReadonlyArray<SimPhaseSample>): SimPhaseSample[] {
  return [...history].sort((a, b) => (asMs(a.atUtc) ?? 0) - (asMs(b.atUtc) ?? 0));
}

export function historyEverAirborne(history: ReadonlyArray<SimPhaseSample> = []): boolean {
  return history.some((s) => sampleIsAirborne(s));
}

/**
 * Where the tail actually is. SimConnect fix first, then the remembered park
 * (Outcome E keeps the last known parking field as truth after a drop).
 */
export function observeSimPresence(input: {
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  aircraft?: Pick<AircraftSpec, "lastAirportIcao"> | null;
  simHistory?: ReadonlyArray<SimPhaseSample>;
  maxNm?: number;
  now?: Date;
}): SimPresence {
  const now = input.now ?? new Date();
  const history = sortedHistory(input.simHistory ?? []);
  const last = history[history.length - 1] ?? null;
  const sawAirborne = historyEverAirborne(history);
  const fused = input.fused ?? null;
  const scenery = [...(input.scenery ?? [])];
  const maxNm = input.maxNm ?? FIELD_PRESENCE_NM;

  const airborne =
    fused?.onGround === false ? true : fused?.onGround === true ? false : last ? sampleIsAirborne(last) : false;

  const near = fused && scenery.length ? nearestDestination(fused, scenery, 400) : null;
  const nearestIcao = near?.airport.icao ? icaoKey(near.airport.icao) : null;
  const distanceNm = near ? Math.round(near.distanceNm * 10) / 10 : null;
  const parked = icaoKey(input.aircraft?.lastAirportIcao) || null;

  if (fused) {
    const atIcao = !airborne && nearestIcao && (distanceNm ?? Number.POSITIVE_INFINITY) <= maxNm ? nearestIcao : null;
    const fixMs = asMs(fused.atUtc);
    const ageMs = fixMs == null ? null : Math.max(0, now.getTime() - fixMs);
    const live = fused.source !== "last_known" && !fused.stale;
    return {
      atIcao: atIcao ?? (airborne ? null : parked && !scenery.length ? parked : null),
      nearestIcao,
      distanceNm,
      airborne,
      known: true,
      fromSim: true,
      live,
      trusted: live || (ageMs != null && ageMs <= PRESENCE_TRUST_MS),
      ageMs,
      source: fused.source,
      atUtc: fused.atUtc ?? null,
      sawAirborne,
    };
  }

  if (parked) {
    return {
      atIcao: parked,
      nearestIcao: parked,
      distanceNm: null,
      airborne: false,
      known: true,
      fromSim: false,
      live: false,
      trusted: false,
      ageMs: null,
      source: "tail_record",
      atUtc: last?.atUtc ?? null,
      sawAirborne,
    };
  }

  return {
    atIcao: null,
    nearestIcao: null,
    distanceNm: null,
    airborne,
    known: Boolean(last),
    fromSim: false,
    live: false,
    trusted: false,
    ageMs: null,
    source: null,
    atUtc: last?.atUtc ?? null,
    sawAirborne,
  };
}

/** Is the tail sitting at this field? `unknown` when the sim cannot say. */
export function fieldPresence(icao: string | null | undefined, presence: SimPresence): MatchState {
  const field = icaoKey(icao);
  if (!field || !presence.known) return "unknown";
  if (presence.atIcao) return sameIcao(presence.atIcao, field) ? "match" : "mismatch";
  if (presence.airborne) return "unknown";
  if (presence.nearestIcao && !sameIcao(presence.nearestIcao, field)) return "mismatch";
  return "unknown";
}

/** Filed OFP origin/dest vs the ticket. */
export function planOdMatch(
  mission: Pick<Mission, "originIcao" | "destIcao">,
  ofp?: MissionOfpAttachment | null,
): MatchState {
  const origin = icaoKey(ofp?.originIcao);
  const dest = icaoKey(ofp?.destIcao);
  if (!origin || !dest) return "unknown";
  return sameIcao(origin, mission.originIcao) && sameIcao(dest, mission.destIcao) ? "match" : "mismatch";
}

function normCall(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s._-]+/g, "");
}

/** SayIntentions OD (or callsign) vs the ticket. Corroboration only. */
export function siOdMatch(
  mission: Pick<Mission, "originIcao" | "destIcao" | "callsign" | "registration">,
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null,
): MatchState {
  if (!si) return "unknown";
  const origin = icaoKey(si.originIcao);
  const dest = icaoKey(si.destIcao);
  if (origin && dest) {
    if (sameIcao(origin, mission.originIcao) && sameIcao(dest, mission.destIcao)) return "match";
    return "mismatch";
  }
  const want = [mission.callsign].map(normCall).filter(Boolean);
  const have = [si.callsign, si.callsignIcao].map(normCall).filter(Boolean);
  if (!want.length || !have.length) return "unknown";
  return want.some((w) => have.includes(w)) ? "match" : "unknown";
}

/**
 * Airborne fix on the ticket's origin→dest corridor. Answers "is the sim going
 * where this ticket says", without pretending to be a flight-plan tracker.
 */
export function routeCorridorMatch(input: {
  mission: Pick<Mission, "originIcao" | "destIcao">;
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  airborne: boolean;
}): MatchState {
  if (!input.airborne || !input.fused) return "unknown";
  const scenery = [...(input.scenery ?? [])];
  if (!scenery.length) return "unknown";
  const origin = resolveCoords(icaoKey(input.mission.originIcao), scenery);
  const dest = resolveCoords(icaoKey(input.mission.destIcao), scenery);
  if (!origin || !dest) return "unknown";
  const legNm = haversineNm(origin.lat, origin.lon, dest.lat, dest.lon);
  if (!Number.isFinite(legNm) || legNm <= 0) return "unknown";
  const viaFix =
    haversineNm(origin.lat, origin.lon, input.fused.lat, input.fused.lon) +
    haversineNm(input.fused.lat, input.fused.lon, dest.lat, dest.lon);
  if (!Number.isFinite(viaFix)) return "unknown";
  const budget = Math.max(OD_CORRIDOR_MIN_NM, legNm * OD_CORRIDOR_FACTOR + OD_CORRIDOR_SLACK_NM);
  return viaFix <= budget ? "match" : "mismatch";
}

/**
 * Did this ticket already land, on its own record? Sim-driven Arrived (or a
 * filed arrival) counts; a bare pending ticket does not.
 */
export function ticketArrivalRecorded(
  mission: Pick<Mission, "id" | "destIcao" | "lifecycle" | "arrivedAtUtc">,
  logs: ReadonlyArray<FlightLogEntry> = [],
): boolean {
  if (mission.arrivedAtUtc && (mission.lifecycle === "arrived" || mission.lifecycle === "diverted")) return true;
  if (mission.arrivedAtUtc && mission.lifecycle === "completed") return true;
  return logs.some((l) => l.tripId === mission.id && Boolean(l.arrivedAtUtc));
}

/** Recorded departure / arrival on this trip's own log row. */
export function logOdMatch(
  mission: Pick<Mission, "id" | "destIcao">,
  logs: ReadonlyArray<FlightLogEntry> = [],
): MatchState {
  const row = logs.find((l) => l.tripId === mission.id);
  if (!row) return "unknown";
  if (row.arrivedAtUtc && sameIcao(row.destIcao, mission.destIcao)) return "match";
  if (row.departedAtUtc) return "unknown";
  return "unknown";
}

/**
 * Old mint bugs left ids like `CEL1007F7` on the board. Those tickets must not
 * claim a sim session or a ledger line for a flight somebody else flew.
 */
export function suspectTicketIdentity(
  mission: Pick<Mission, "id" | "callsign" | "flightNumber" | "flightType">,
  board: ReadonlyArray<Pick<Mission, "id" | "callsign" | "flightNumber">> = [],
): boolean {
  const label = displayFlightNumber(mission).trim();
  if (!label) return false;
  const parts = parseFlightNumber(label);
  if (!parts) return /^CEL/i.test(label.replace(/[\s._-]+/g, ""));
  if (parts.seq < FIRST_FLIGHT_SEQ) return true;
  if (!parts.ferry) return false;
  const siblings = board.filter((m) => {
    const other = parseFlightNumber(displayFlightNumber(m));
    return Boolean(other && other.ferry && other.seq === parts.seq);
  });
  const known = siblings.length || 1;
  return parts.ferryIndex >= known;
}

function pushSource(sources: GroundingSource[], source: GroundingSource): void {
  if (!sources.includes(source)) sources.push(source);
}

function routeLabel(mission: Pick<Mission, "originIcao" | "destIcao">): string {
  return `${icaoKey(mission.originIcao)}–${icaoKey(mission.destIcao)}`;
}

/**
 * Layered evidence for one ticket. Facts first — the gates below decide what
 * the desk is allowed to do with them.
 */
export function groundTicket(input: GroundTicketInput): TicketGrounding {
  const mission = input.mission;
  const aircraft =
    input.aircraft ??
    input.fleet?.find((a) => a.id === mission.aircraftId || a.registration === mission.registration) ??
    null;
  const presence = observeSimPresence({
    fused: input.fused,
    scenery: input.scenery,
    aircraft,
    simHistory: input.simHistory,
  });

  const atOrigin = fieldPresence(mission.originIcao, presence);
  const atDest = fieldPresence(mission.destIcao, presence);
  const route = routeCorridorMatch({
    mission,
    fused: input.fused,
    scenery: input.scenery,
    airborne: presence.airborne,
  });
  const plan = planOdMatch(mission, input.ofp ?? mission.ofpAttachment ?? null);
  const si = siOdMatch(mission, input.si);
  const log = logOdMatch(mission, input.logs);
  const arrivalRecorded = ticketArrivalRecorded(mission, input.logs);

  const sources: GroundingSource[] = [];
  if (presence.known) pushSource(sources, "simconnect");
  if (plan !== "unknown") pushSource(sources, "flight_plan");
  if (si !== "unknown") pushSource(sources, "sayintentions");
  if (log !== "unknown") pushSource(sources, "flight_log");

  const conflicts: string[] = [];
  if (plan === "mismatch") {
    const ofp = input.ofp ?? mission.ofpAttachment;
    conflicts.push(
      `filed plan is ${icaoKey(ofp?.originIcao)}–${icaoKey(ofp?.destIcao)}, ticket is ${routeLabel(mission)}`,
    );
  }
  if (si === "mismatch") {
    conflicts.push(
      `SayIntentions is flying ${icaoKey(input.si?.originIcao)}–${icaoKey(input.si?.destIcao)}, ticket is ${routeLabel(mission)}`,
    );
  }
  if (route === "mismatch") {
    conflicts.push(`sim track is not on ${routeLabel(mission)}`);
  }

  const agreed = atDest === "match" && plan !== "mismatch" && si !== "mismatch" && route !== "mismatch";
  const verdict: GroundingVerdict = conflicts.length ? "mismatch" : agreed ? "grounded" : "unverified";

  const where = presence.atIcao
    ? `at ${presence.atIcao}`
    : presence.airborne
      ? "airborne"
      : presence.known
        ? "position unknown"
        : "no sim";
  const status =
    verdict === "mismatch"
      ? `SIM MISMATCH  ${displayFlightNumber(mission) || mission.callsign} ${routeLabel(mission)} — ${conflicts[0]}`
      : verdict === "grounded"
        ? `GROUNDED  ${displayFlightNumber(mission) || mission.callsign} ${routeLabel(mission)} — sim ${where}`
        : `UNVERIFIED  ${displayFlightNumber(mission) || mission.callsign} ${routeLabel(mission)} — sim ${where}`;

  return {
    missionId: mission.id,
    flightNumber: displayFlightNumber(mission) || mission.callsign,
    originIcao: icaoKey(mission.originIcao),
    destIcao: icaoKey(mission.destIcao),
    verdict,
    atOrigin,
    atDest,
    route,
    plan,
    si,
    log,
    arrivalRecorded,
    presence,
    sources,
    conflicts,
    status,
  };
}

export type PickCurrentFlightInput = PickCurrentTicketInput & {
  fused?: FusedPosition | null;
  previousFused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  /** Desk-stored OFP / SI pair. SimConnect does not read SimBrief cloud live. */
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null;
  now?: Date;
  activeFlightId?: string | null;
  activeFlightAuthority?: FlightBindingAuthority | null;
  lastConfidentId?: string | null;
  lastParkedIcao?: string | null;
  fleet?: ReadonlyArray<Pick<AircraftSpec, "registration" | "lastAirportIcao">>;
};

function isActiveOrAccepted(mission: Mission, assignment?: Pick<TripAssignment, "status"> | null): boolean {
  const life = ticketLifecycle(mission, assignment);
  return (
    mission.status === "in-progress" ||
    life === "airborne" ||
    life === "taxi" ||
    life === "arrived" ||
    life === "diverted" ||
    life === "accepted"
  );
}

function corridorDetourRatio(
  mission: Pick<Mission, "originIcao" | "destIcao">,
  fused: FusedPosition | null | undefined,
  scenery: ReadonlyArray<SceneryAirport> | undefined,
): number | null {
  if (!fused) return null;
  const origin = resolveCoords(icaoKey(mission.originIcao), [...(scenery ?? [])]);
  const dest = resolveCoords(icaoKey(mission.destIcao), [...(scenery ?? [])]);
  if (!origin || !dest) return null;
  const legNm = haversineNm(origin.lat, origin.lon, dest.lat, dest.lon);
  if (!Number.isFinite(legNm) || legNm <= 0) return null;
  const viaFix =
    haversineNm(origin.lat, origin.lon, fused.lat, fused.lon) +
    haversineNm(fused.lat, fused.lon, dest.lat, dest.lon);
  if (!Number.isFinite(viaFix)) return null;
  return viaFix / legNm;
}

/**
 * Does this ticket agree with the live sim / desk-stored OFP pair?
 * Position corridor and ticket OFP origin–dest only — no SimBrief cloud fetch.
 */
export function ticketRouteAgrees(input: {
  mission: Mission;
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null;
  ofp?: MissionOfpAttachment | null;
  now?: Date;
}): boolean {
  return ticketRouteAgreementScore(input) > 0;
}

export function ticketRouteAgreementScore(input: {
  mission: Mission;
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  si?: Pick<SiFlightSession, "originIcao" | "destIcao" | "callsign" | "callsignIcao"> | null;
  ofp?: MissionOfpAttachment | null;
  now?: Date;
  lastParkedIcao?: string | null;
}): number {
  if (suspectTicketIdentity(input.mission)) return -1;
  const fused = isNarniaGroundSpawn(input.fused, input.lastParkedIcao, input.scenery) ? null : input.fused;
  const grounding = groundTicket({
    mission: input.mission,
    fused,
    scenery: input.scenery,
    si: input.si,
    ofp: input.ofp ?? input.mission.ofpAttachment ?? null,
    now: input.now,
  });
  // A stored OFP on this ticket that is a different pair is not this flight.
  if (grounding.plan === "mismatch") return -1;
  if (grounding.route === "mismatch") return -1;
  let score = 0;
  if (grounding.route === "match") score += 2;
  if (grounding.atOrigin === "match") score += 2;
  if (grounding.si === "match") score += 2;
  if (grounding.plan === "match") score += 3;
  if (score === 0) return 0;
  const detour = corridorDetourRatio(input.mission, fused, input.scenery);
  if (detour != null) score += Math.max(0, 2 - detour);
  if (isActiveOrAccepted(input.mission)) score += 1;
  return score;
}

/**
 * Current flight for SIM / lifecycle. Human/Megan Mark Active wins even with
 * no SimConnect. Live multi-signal jury next. Schedule is last — never an
 * invented reverse pair.
 */
function lastParkedFromInput(input: PickCurrentFlightInput): string {
  const explicit = icaoKey(input.lastParkedIcao);
  if (explicit) return explicit;
  const fleet = input.fleet ?? [];
  const remembered = fleet.find((a) => icaoKey(a.lastAirportIcao));
  return icaoKey(remembered?.lastAirportIcao);
}

export function pickCurrentFlightTicket(input: PickCurrentFlightInput): Mission | null {
  const scoped = [...input.missions];
  const lastParkedIcao = lastParkedFromInput(input);
  return resolveCurrentFlight({
    ...input,
    missions: scoped,
    lastParkedIcao,
    scoreTicket: (mission) => {
      if (suspectTicketIdentity(mission, scoped)) return -1;
      return ticketRouteAgreementScore({
        mission,
        fused: input.fused,
        scenery: input.scenery,
        si: input.si,
        now: input.now,
        lastParkedIcao,
      });
    },
  }).mission;
}

/**
 * Lifecycle may still move when the sim cannot say (no scenery, SI-only fix).
 * It may never move against evidence — a wrong-route guess stops here.
 */
export function lifecycleBlock(grounding: TicketGrounding, to: "depart" | "arrive"): string {
  if (grounding.conflicts.length) return grounding.conflicts[0]!;
  const where = grounding.presence.atIcao ?? grounding.presence.nearestIcao ?? "an unknown field";
  if (to === "depart" && grounding.atOrigin === "mismatch") {
    return `aircraft is at ${where}, not ${grounding.originIcao}`;
  }
  if (to === "arrive" && grounding.atDest === "mismatch") {
    return `aircraft is at ${where}, not ${grounding.destIcao}`;
  }
  return "";
}

/**
 * Completed (and with it charter revenue / cost as flown) needs the sim to
 * agree. Fails closed on mismatch and on "never left the gate".
 */
export function completionBlock(grounding: TicketGrounding): string {
  if (grounding.conflicts.length) return grounding.conflicts[0]!;
  const presence = grounding.presence;
  if (grounding.atDest === "match") return "";
  // This ticket already landed on the record. The tail flying on to the next
  // sector does not retroactively un-fly it — its ground fees stand.
  if (grounding.arrivalRecorded || grounding.log === "match") return "";
  // The remembered tail park is not enough to call a desk wrong — only a real,
  // recent fix contradicts. A desk flying without SimConnect still completes.
  if (!presence.trusted) return "";
  if (grounding.atOrigin === "match" && !presence.sawAirborne) {
    return `sim never left ${presence.atIcao} — this ticket has not been flown`;
  }
  if (grounding.atDest === "mismatch" && presence.live) {
    return `aircraft is at ${presence.atIcao}, not ${grounding.destIcao}`;
  }
  return "";
}

/** Charter revenue / parking may only post against a flight the evidence backs. */
export function canPostFlownCosts(grounding: TicketGrounding): boolean {
  if (completionBlock(grounding)) return false;
  return grounding.atDest === "match" || grounding.log === "match";
}

/**
 * Was the tail ever actually at this field? Either it is sitting there now
 * (live fix, last-known park after a SimConnect drop, or the remembered tail
 * record — Outcome E keeps that as truth), or it has since departed from it,
 * which only a real visit produces. A schedule that merely names the field
 * never counts.
 */
export function fieldEverVisited(input: {
  icao: string;
  presence: SimPresence;
  logs?: ReadonlyArray<FlightLogEntry>;
  aircraft?: Pick<AircraftSpec, "id" | "registration" | "lastAirportIcao"> | null;
  registration?: string;
}): boolean {
  const field = icaoKey(input.icao);
  if (!field) return false;
  if (sameIcao(input.presence.atIcao, field)) return true;
  if (sameIcao(input.aircraft?.lastAirportIcao, field)) return true;
  const tail = (input.registration || input.aircraft?.registration || "").trim().toUpperCase();
  return (input.logs ?? []).some((l) => {
    if (!l.departedAtUtc) return false;
    if (!sameIcao(l.originIcao, field)) return false;
    if (!tail) return true;
    return (l.registration ?? "").trim().toUpperCase() === tail;
  });
}

/** Desk-facing "the schedule wanted this, the sim did not back it" row. */
export type GroundingHold = {
  missionId: string;
  flightNumber: string;
  kind: "hotel" | "parking" | "complete";
  icao: string;
  reason: string;
  /** A ghost line that had already posted and was taken back off the ledger. */
  voided: boolean;
  status: string;
  atUtc: string;
};

export type HotelGroundingInput = {
  icao: string;
  grounding: TicketGrounding;
  logs?: ReadonlyArray<FlightLogEntry>;
  aircraft?: Pick<AircraftSpec, "id" | "registration" | "lastAirportIcao"> | null;
};

/**
 * Overnight crew hotel at a field: the tail has to actually be sitting there,
 * or to have been there and since departed. A schedule that says it will be
 * is not enough — that is how a Palm Beach night appears for a sector nobody
 * flew. The stay still sticks across a SimConnect drop: last-known park is
 * truth, so a closed sim keeps the night, it does not cancel it.
 */
export function hotelBlock(input: HotelGroundingInput): string {
  const field = icaoKey(input.icao);
  const presence = input.grounding.presence;
  if (!field) return "no overnight field";
  if (input.grounding.conflicts.length) return input.grounding.conflicts[0]!;
  if (!presence.known) return `no sim position — cannot confirm the aircraft overnights at ${field}`;
  if (fieldPresence(field, presence) === "match") return "";
  if (
    fieldEverVisited({
      icao: field,
      presence,
      logs: input.logs,
      aircraft: input.aircraft,
    })
  ) {
    return "";
  }
  if (presence.airborne) return `aircraft is airborne and has not been at ${field}`;
  return `aircraft is at ${presence.atIcao ?? presence.nearestIcao ?? "an unknown field"}, not ${field}`;
}

export function canPostOvernightHotel(input: HotelGroundingInput): boolean {
  return hotelBlock(input) === "";
}
